import type { ScheduleItem } from '../types/index.js'

/**
 * Déroulé estimé d'un bloc de direct, côté interface.
 *
 * Un direct occupe un seul élément de grille : rien n'est planifié à
 * l'intérieur, c'est le décrochage qui y creuse les coupures en cours
 * d'émission. Le déroulé estimé est l'annotation qui redonne à la régie et à
 * l'EPG le repère perdu par ce choix : « on rend l'antenne dans 6 minutes »
 * plutôt qu'un seul compte à rebours vers 19 h.
 *
 * Miroir exact de `backend/src/lib/live-part-plan.ts`, en millisecondes plutôt
 * qu'en `Date` — c'est l'unité du reste des compteurs de l'interface
 * (`lib/clock.ts`). Les deux modules doivent rester d'accord : ce que la régie
 * lit est ce que le partenaire a reçu. Les bornes ci-dessous sont celles que le
 * serveur refuse, répétées ici pour que la saisie le dise avant d'envoyer.
 */

/** Miroir de `LIVE_PART_MAX_COUNT`. */
export const LIVE_PART_MAX_COUNT = 12

/** Miroir de `LIVE_PART_MIN_SEC`. */
export const LIVE_PART_MIN_SEC = 30

/** Miroir de `LIVE_PART_MAX_BREAK_SEC`. */
export const LIVE_PART_MAX_BREAK_SEC = 3600

export const LIVE_PART_MAX_TITLE_LENGTH = 80

/** Partie telle qu'elle est saisie, avant tout calage. */
export interface LivePartDraft {
  title:         string | null
  durationSec:   number
  breakAfterSec: number
  /** Durée ajustée par la régie pendant l'antenne ; absente ou `null` = automatique. */
  onAirDurationSec?: number | null
}

/** Partie calée sur l'antenne. */
export interface LivePartSegment {
  /** Rang affiché et publié, 1-based. */
  number:        number
  title:         string | null
  startMs:       number
  endMs:         number
  /** Durée réellement tenue par le bloc. */
  durationSec:   number
  /** Durée retenue pour l'estimation : ajustée, annoncée, ou reste du bloc. */
  plannedSec:    number
  breakAfterSec: number
  truncated:     boolean
  /** Rang dans le déroulé saisi, 0-based — stable même si une partie est retirée. */
  partIndex?:    number
  /** Durée annoncée au déroulé (celle de l'EPG). */
  announcedSec?: number
  /** Durée fixée par la régie pendant l'antenne, `null` en automatique. */
  adjustedSec?:  number | null
  /** Vrai pour la dernière partie en automatique : elle court jusqu'à la fin du bloc. */
  absorbs?:      boolean
}

export interface LivePartTimeline {
  segments:     LivePartSegment[]
  plannedEndMs: number
  /** Temps de bloc laissé libre après le déroulé. */
  slackSec:     number
  /** Dépassement du déroulé au-delà du bloc : c'est ce que le serveur refuse. */
  overflowSec:  number
  droppedCount: number
}

/** Durée totale annoncée : parties et coupures intermédiaires. */
export function livePartPlanTotalSec(parts: readonly LivePartDraft[]): number {
  return parts.reduce((total, part, index) => {
    const breakSec = index === parts.length - 1 ? 0 : Math.max(0, Math.round(part.breakAfterSec))
    return total + Math.max(0, Math.round(part.durationSec)) + breakSec
  }, 0)
}

/**
 * Cale le déroulé sur l'antenne. Le bloc reste l'autorité : une partie qui le
 * déborde est raccourcie, celles qui ne commencent plus avant sa fin sont
 * retirées.
 */
export function buildLivePartTimeline(
  parts: readonly LivePartDraft[],
  anchorMs: number,
  blockEndMs: number,
): LivePartTimeline {
  const segments: LivePartSegment[] = []
  let cursor = anchorMs
  let dropped = 0

  parts.forEach((part, index) => {
    const plannedSec = Math.max(0, Math.round(part.durationSec))
    if (cursor >= blockEndMs) { dropped++; return }

    const startMs = cursor
    const plannedEndMs = startMs + plannedSec * 1000
    const endMs = Math.min(plannedEndMs, blockEndMs)
    const breakSec = index === parts.length - 1 ? 0 : Math.max(0, Math.round(part.breakAfterSec))

    segments.push({
      number:        segments.length + 1,
      title:         part.title?.trim() ? part.title.trim() : null,
      startMs,
      endMs,
      durationSec:   Math.round((endMs - startMs) / 1000),
      plannedSec,
      breakAfterSec: Math.min(breakSec, Math.max(0, Math.round((blockEndMs - endMs) / 1000))),
      truncated:     plannedEndMs > blockEndMs,
    })
    cursor = endMs + breakSec * 1000
  })

  const overshootMs = anchorMs + livePartPlanTotalSec(parts) * 1000 - blockEndMs
  return {
    segments,
    plannedEndMs: segments.length ? segments[segments.length - 1].endMs : anchorMs,
    slackSec:     Math.max(0, Math.round(-overshootMs / 1000)),
    overflowSec:  Math.max(0, Math.round(overshootMs / 1000)),
    droppedCount: dropped,
  }
}

export type LivePartPhase = 'BEFORE' | 'PART' | 'BREAK' | 'AFTER'

export interface LivePartCursor {
  phase:       LivePartPhase
  /** Partie en cours, ou celle qui vient de s'achever pendant une coupure. */
  current:     LivePartSegment | null
  next:        LivePartSegment | null
  /**
   * Temps restant sur la partie en cours. `null` quand il n'y a rien à
   * décompter : avant que le bloc n'ait pris l'antenne on n'en sait rien, et
   * pendant une coupure le compteur ne repart qu'au raccrochage.
   */
  remainingMs: number | null
  partCount:   number
}

/** Parties enregistrées d'un élément, dans l'ordre, vides si le direct n'en a pas. */
export function livePartDrafts(item: Pick<ScheduleItem, 'liveParts'> | null | undefined): LivePartDraft[] {
  return [...(item?.liveParts ?? [])]
    .sort((a, b) => a.partIndex - b.partIndex)
    .map(part => ({
      title:            part.title,
      durationSec:      part.durationSec,
      breakAfterSec:    part.breakAfterSec,
      onAirDurationSec: part.onAirDurationSec ?? null,
    }))
}

/**
 * Déroulé prévu d'un élément, calé sur l'antenne. C'est la lecture de la fiche
 * en agenda : ce que l'opérateur a annoncé, avant tout décrochage.
 *
 * L'ancre est le **top réel** quand il existe (`liveGoLiveAt`) : un direct parti
 * en retard sur un top manuel décale tout son déroulé. L'export EPG, lui, publie
 * la grille prévue et ancre sur l'heure de grille.
 */
export function liveItemPartTimeline(item: ScheduleItem): LivePartTimeline | null {
  const parts = livePartDrafts(item)
  if (!parts.length) return null
  const anchorMs = new Date(item.liveGoLiveAt ?? item.startTime).getTime()
  return buildLivePartTimeline(parts, anchorMs, new Date(item.endTime).getTime())
}

type LiveBlockRef = Pick<ScheduleItem, 'bundleId' | 'programId' | 'liveSourceId'>

/**
 * Deux segments du même bloc de direct. Même règle que le raccrochage côté
 * serveur (`lib/live-break-plan.ts`) : le `bundleId` fait foi, la paire
 * programme + source ne sert qu'aux blocs anciens ou créés par l'API sans
 * bundle, et deux lignes entièrement anonymes ne sont jamais liées.
 */
function sameLiveBlock(a: LiveBlockRef, b: LiveBlockRef): boolean {
  if (a.bundleId || b.bundleId) return Boolean(a.bundleId) && a.bundleId === b.bundleId
  const hasIdentity = a.programId !== null || a.liveSourceId !== null
  return hasIdentity && a.programId === b.programId && a.liveSourceId === b.liveSourceId
}

/** Passage à l'antenne d'un segment de direct : le top réel fait foi sur la grille. */
export interface LiveAiring {
  startMs: number
  endMs:   number
  /**
   * Faux tant que le segment n'a pas pris l'antenne. Un direct manuel dont le
   * top tarde ne fait pas courir sa partie : l'heure de grille est passée, pas
   * l'émission.
   */
  aired:   boolean
}

/** Segments jamais passés à l'antenne : ils ne consomment pas de partie. */
const AIRED_EXCLUDED_STATUSES = ['SKIPPED', 'ERROR']

/** Segments d'un même bloc de direct, dans l'ordre de passage. */
function liveBlockSegments(item: ScheduleItem, items: readonly ScheduleItem[]): ScheduleItem[] {
  const segments = items
    .filter(candidate => candidate.itemType === 'LIVE'
      && !AIRED_EXCLUDED_STATUSES.includes(candidate.status)
      && sameLiveBlock(item, candidate))
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
  return segments.length ? segments : [item]
}

/**
 * Un segment a-t-il pris l'antenne ?
 *
 * Un segment à départ automatique — toute reprise de décrochage l'est — prend
 * l'antenne à son heure de grille : l'heure passée suffit. Attendre le seul
 * statut `ON_AIR` figeait le compteur de la partie suivante : au raccrochage,
 * le `GRID_UPDATED` recharge la grille pendant que le moteur envoie
 * `ITEM_STATUS`, et un statut arrivé avant le segment était perdu jusqu'au
 * rafraîchissement de la page. Un départ manuel, lui, attend son top.
 */
function airingOf(segment: ScheduleItem, nowMs: number): LiveAiring {
  const startMs = new Date(segment.liveGoLiveAt ?? segment.startTime).getTime()
  return {
    startMs,
    endMs:   new Date(segment.endTime).getTime(),
    aired:   segment.status === 'ON_AIR'
      || segment.status === 'DONE'
      || segment.liveGoLiveAt != null
      || (segment.liveStartMode !== 'MANUAL' && startMs <= nowMs),
  }
}

/** Types à heure fixe : ils bornent une suite de comblage (`lib/liveBreakState.ts`). */
const ANCHOR_TYPES = ['PROGRAM', 'LIVE']

/** Tolérance d'adjacence d'une suite de comblage, comme le raccrochage côté serveur. */
const ADJACENCY_TOLERANCE_MS = 1_500

/**
 * Fin du bloc de direct, décrochage compris.
 *
 * Tant qu'un segment tient l'antenne, c'est la fin du dernier segment. Pendant
 * un décrochage sans durée fixe, aucune reprise n'est posée : le bloc court
 * alors jusqu'à la fin de la suite de comblage qui suit le dernier segment —
 * celle jusqu'à laquelle le raccrochage recréera le direct. Sans cela, le
 * déroulé s'arrêtait sur le décrochage et annonçait « Déroulé terminé » avant
 * même la partie 2.
 */
function liveBlockEndMs(block: readonly ScheduleItem[], items: readonly ScheduleItem[], nowMs: number): number {
  let endMs = block.reduce((latest, segment) => Math.max(latest, new Date(segment.endTime).getTime()), 0)
  if (nowMs < endMs) return endMs
  const following = items
    .filter(item => new Date(item.startTime).getTime() >= endMs - ADJACENCY_TOLERANCE_MS)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
  for (const item of following) {
    const startMs = new Date(item.startTime).getTime()
    if (startMs - endMs > ADJACENCY_TOLERANCE_MS) break
    if (ANCHOR_TYPES.includes(item.itemType)) break
    endMs = Math.max(endMs, new Date(item.endTime).getTime())
  }
  return endMs
}

export interface LiveBlockRundown {
  /** Une case par partie : celles déjà passées à l'antenne, puis les projetées. */
  segments:   LivePartSegment[]
  cursor:     LivePartCursor
  /** Fin du bloc entier, décrochages compris : l'autorité sur le déroulé. */
  blockEndMs: number
}

/**
 * Déroulé d'un bloc de direct **recalé sur l'antenne réelle**.
 *
 * Le déroulé saisi ne donne que des durées ; ce sont les décrochages qui disent
 * quand chaque partie commence vraiment. La règle est donc uniforme : la partie
 * N est calée sur le N-ième segment de direct du bloc, dont le début est le top
 * réel (`liveGoLiveAt`, sinon l'heure de grille) et la fin l'instant du
 * décrochage. Le compteur d'une partie court donc depuis le raccrochage, pas
 * depuis une heure calculée d'avance : un décrochage plus long ou plus court que
 * prévu ne décale plus rien.
 *
 * Tant que l'antenne n'est pas rendue au direct, il n'y a rien à décompter —
 * le compteur est `null` et l'interface affiche « —:— ». Les parties pas encore
 * jouées restent projetées derrière la dernière connue, jamais dans le passé :
 * elles ne servent qu'à dire « puis partie 3 » et à dessiner la barre.
 *
 * Deux écarts possibles entre le déroulé et l'antenne, tranchés de la même
 * façon : plus de décrochages que de parties annoncées, et le déroulé est
 * simplement terminé ; plus de parties que de décrochages, et les dernières
 * restent projetées sans jamais commencer. Rien de tout cela ne commande quoi
 * que ce soit à l'antenne : le déroulé reste une estimation.
 */
export function buildLiveBlockRundown(
  parts:      readonly LivePartDraft[],
  airings:    readonly LiveAiring[],
  blockEndMs: number,
  nowMs:      number,
): LiveBlockRundown {
  const segments: LivePartSegment[] = []
  let projected = airings.length ? airings[0].startMs : nowMs

  parts.forEach((part, index) => {
    const announcedSec = Math.max(0, Math.round(part.durationSec))
    const adjustedSec = part.onAirDurationSec == null ? null : Math.max(0, Math.round(part.onAirDurationSec))
    // Recalcul automatique : les parties gardent leur durée (ajustée, sinon
    // annoncée) et la dernière, laissée en automatique, absorbe l'écart avec la
    // fin du bloc — décrochages plus longs ou plus courts compris.
    const absorbs = index === parts.length - 1 && adjustedSec == null
    const fixedSec = adjustedSec ?? announcedSec
    // Un segment qui n'a pas encore pris l'antenne ne cale rien : sa partie est
    // projetée comme les autres, jusqu'au top ou au raccrochage.
    const planned = airings[index] ?? null
    const airing = planned?.aired ? planned : null
    // Le bloc reste l'autorité : une partie que le déroulé ne fait plus
    // commencer avant sa fin est retirée plutôt qu'affichée à durée nulle.
    if (!airing && projected >= blockEndMs) return
    // Une partie déjà passée est calée sur son segment ; une partie à venir est
    // projetée derrière la précédente, jamais dans le passé — sans quoi une
    // coupure qui s'éternise afficherait un retour déjà dépassé.
    const startMs = airing ? airing.startMs : Math.max(projected, nowMs)
    if (startMs >= blockEndMs) return

    const plannedEndMs = absorbs ? blockEndMs : startMs + fixedSec * 1000
    const plannedSec = Math.round((plannedEndMs - startMs) / 1000)
    // Le segment fait foi dès qu'il est terminé : c'est là que le décrochage a
    // réellement coupé la partie, qu'elle ait tenu son estimation ou non.
    const endMs = Math.min(airing && airing.endMs <= nowMs ? airing.endMs : plannedEndMs, blockEndMs)
    const breakSec = index === parts.length - 1 ? 0 : Math.max(0, Math.round(part.breakAfterSec))

    segments.push({
      number:        segments.length + 1,
      title:         part.title?.trim() ? part.title.trim() : null,
      startMs,
      endMs,
      durationSec:   Math.round((endMs - startMs) / 1000),
      plannedSec,
      breakAfterSec: Math.min(breakSec, Math.max(0, Math.round((blockEndMs - endMs) / 1000))),
      truncated:     plannedEndMs > blockEndMs,
      partIndex:     index,
      announcedSec,
      adjustedSec,
      absorbs,
    })
    projected = endMs + breakSec * 1000
  })

  return { segments, blockEndMs, cursor: liveBlockCursor(segments, airings, nowMs) }
}

const IDLE_CURSOR: LivePartCursor = { phase: 'AFTER', current: null, next: null, remainingMs: null, partCount: 0 }

/** Où en est le déroulé : la seule lecture du chrono et du widget Clock. */
function liveBlockCursor(
  segments: readonly LivePartSegment[],
  airings:  readonly LiveAiring[],
  nowMs:    number,
): LivePartCursor {
  const partCount = segments.length
  if (!partCount) return IDLE_CURSOR

  const onAirIndex = airings.findIndex(airing => airing.aired && nowMs >= airing.startMs && nowMs < airing.endMs)
  if (onAirIndex >= 0) {
    // Plus de décrochages que de parties annoncées : le déroulé est derrière nous.
    if (onAirIndex >= partCount) {
      return { phase: 'AFTER', current: segments[partCount - 1], next: null, remainingMs: null, partCount }
    }
    const current = segments[onAirIndex]
    return {
      phase:       'PART',
      current,
      next:        segments[onAirIndex + 1] ?? null,
      // Une partie qui déborde son estimation reste la partie en cours : le
      // compteur tombe à zéro et y reste, il ne commande rien.
      remainingMs: Math.max(0, current.endMs - nowMs),
      partCount,
    }
  }

  const airedCount = airings.filter(airing => airing.aired && airing.startMs <= nowMs).length
  if (!airedCount) {
    // Le bloc n'a pas pris l'antenne. Avant l'heure de grille on annonce le
    // top ; passé cette heure sur un top manuel qui tarde, il n'y a plus rien à
    // décompter — la partie 1 commencera quand l'opérateur l'aura décidé.
    const startMs = segments[0].startMs
    return { phase: 'BEFORE', current: null, next: segments[0], remainingMs: startMs > nowMs ? startMs - nowMs : null, partCount }
  }
  if (airedCount >= partCount) {
    return { phase: 'AFTER', current: segments[partCount - 1], next: null, remainingMs: null, partCount }
  }
  // Décrochage en cours : le compteur de la partie suivante ne repart qu'au
  // raccrochage, personne ne sait quand l'antenne sera rendue.
  return { phase: 'BREAK', current: segments[airedCount - 1], next: segments[airedCount], remainingMs: null, partCount }
}

/** Segment du bloc qui porte le déroulé : c'est lui que l'ajustement antenne écrit. */
export function liveBlockCarrier(item: ScheduleItem, items: readonly ScheduleItem[]): ScheduleItem | null {
  return liveBlockSegments(item, items).find(segment => livePartDrafts(segment).length > 0) ?? null
}

/**
 * Conducteur où le déroulé du bloc porte des durées ajustées — aperçu de
 * l'éditeur de régie avant envoi. `adjustments` indexe par `partIndex` ; une
 * valeur `null` rend la partie au calcul automatique, une partie absente garde
 * son réglage actuel.
 */
export function withLivePartAdjustments(
  item:        ScheduleItem,
  items:       readonly ScheduleItem[],
  adjustments: Readonly<Record<number, number | null>>,
): ScheduleItem[] {
  const carrier = liveBlockCarrier(item, items)
  if (!carrier) return [...items]
  const adjusted: ScheduleItem = {
    ...carrier,
    liveParts: (carrier.liveParts ?? []).map(part => part.partIndex in adjustments
      ? { ...part, onAirDurationSec: adjustments[part.partIndex] }
      : part),
  }
  const replaced = items.map(candidate => candidate.id === carrier.id ? adjusted : candidate)
  return replaced.some(candidate => candidate.id === carrier.id) ? replaced : [...replaced, adjusted]
}

/**
 * Déroulé recalé du bloc auquel appartient `item`, ou `null` sans déroulé saisi.
 *
 * Un décrochage découpe le bloc en plusieurs `ScheduleItem` de type `LIVE`, mais
 * le déroulé n'est enregistré que sur le premier : les segments de reprise
 * n'ont aucune ligne `live_part_estimates`. Lire les parties du seul élément à
 * l'antenne fait donc disparaître le déroulé au premier raccrochage — constaté
 * à l'antenne le 16/09/2026 sur deux directs de la nuit.
 */
export function liveBlockRundown(
  item:  ScheduleItem,
  items: readonly ScheduleItem[],
  nowMs: number,
): LiveBlockRundown | null {
  const block = liveBlockSegments(item, items)
  const carrier = liveBlockCarrier(item, items)
  if (!carrier) return null

  const airings = block.map(segment => airingOf(segment, nowMs))
  const blockEndMs = liveBlockEndMs(block, items, nowMs)
  return buildLiveBlockRundown(livePartDrafts(carrier), airings, blockEndMs, nowMs)
}

/**
 * Bloc de direct à lire maintenant : celui qui porte l'antenne, ou celui dont on
 * vient de décrocher.
 *
 * Pendant un décrochage sans durée fixe, aucun segment de reprise n'est encore
 * posé : « le prochain direct » ne désigne alors plus le bloc en cours mais une
 * émission sans rapport, plus tard dans la journée. On repart donc du dernier
 * segment déjà parti à l'antenne, et on ne le retient que si son bloc n'est pas
 * terminé — un bloc coupé net sur un décrochage sans reprise ne compte plus.
 */
export function liveBlockReference(items: readonly ScheduleItem[], nowMs: number): ScheduleItem | null {
  const started = items
    .filter(item => item.itemType === 'LIVE'
      && !AIRED_EXCLUDED_STATUSES.includes(item.status)
      && new Date(item.liveGoLiveAt ?? item.startTime).getTime() <= nowMs)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())

  const last = started[started.length - 1] ?? null
  if (!last) return null

  const blockEndMs = liveBlockEndMs(liveBlockSegments(last, items), items, nowMs)
  return nowMs < blockEndMs ? last : null
}

/**
 * Parties encore ajustables depuis la régie : la partie en cours, puis celles à
 * venir. Pendant un décrochage, la partie qui reviendra au raccrochage ouvre la
 * liste. Vide avant le top et une fois le déroulé terminé.
 */
export function liveAdjustableParts(rundown: LiveBlockRundown): LivePartSegment[] {
  const { cursor, segments } = rundown
  const first = cursor.phase === 'PART' ? cursor.current : cursor.phase === 'BREAK' ? cursor.next : null
  if (!first) return []
  return segments.filter(segment => segment.number >= first.number)
}
